import { useEffect, useState } from "react";
import { FilePlus2, Landmark, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "./docUtils";

const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";
const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
const emptyItem = () => ({ prestation: "", description: "", qty: 1, price_ht: "" });

export default function BillingSettings({ client, onSaved }) {
  const [email, setEmail] = useState(client.billing_email || client.email || "");
  const [contact, setContact] = useState(client.billing_contact || "");
  const [address, setAddress] = useState(client.billing_address || client.brand?.address || "");
  const [siret, setSiret] = useState(client.billing_siret || "");
  const [auto, setAuto] = useState({
    enabled: client.auto_invoice?.enabled || false,
    day: client.auto_invoice?.day || 1,
    auto_send: client.auto_invoice?.auto_send || false,
    title_template: client.auto_invoice?.title_template || "Community management - {{mois_en_cours}}",
  });
  const [items, setItems] = useState(
    (client.auto_invoice?.items || []).map((i) => ({ ...i })));
  const [tva, setTva] = useState(client.auto_invoice?.tva_mode || "non_assujetti");
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const generateNow = async () => {
    setGenBusy(true);
    try {
      const r = await api.post(`/accounting/clients/${client.id}/generate-monthly-invoice`);
      toast.success(`Brouillon créé (${eur(r.data.total_ttc)}) — retrouvez-le dans Comptabilité → Factures`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la génération"); }
    finally { setGenBusy(false); }
  };

  useEffect(() => {
    setEmail(client.billing_email || client.email || "");
    setContact(client.billing_contact || "");
    setAddress(client.billing_address || client.brand?.address || "");
    setSiret(client.billing_siret || "");
    setAuto({
      enabled: client.auto_invoice?.enabled || false,
      day: client.auto_invoice?.day || 1,
      auto_send: client.auto_invoice?.auto_send || false,
      title_template: client.auto_invoice?.title_template || "Community management - {{mois_en_cours}}",
    });
    setItems((client.auto_invoice?.items || []).map((i) => ({ ...i })));
    setTva(client.auto_invoice?.tva_mode || "non_assujetti");
  }, [client.id]);

  const setItem = (i, k, v) => setItems((its) => its.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const itemsTotal = items.reduce((s, it) => s + num(it.qty || 1) * num(it.price_ht), 0);

  const monthlyTotal = (client.billing_items || [])
    .filter((i) => i.frequency === "mensuel")
    .reduce((s, i) => s + (i.amount || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        billing_email: email.trim(), billing_contact: contact.trim(),
        billing_address: address.trim(),
        billing_siret: siret.trim(),
        auto_invoice: { ...auto, tva_mode: tva,
          items: items.filter((it) => it.prestation?.trim()).map((it) => ({
            ...it, qty: num(it.qty || 1), price_ht: num(it.price_ht) })) },
      });
      toast.success("Données de facturation enregistrées");
      onSaved();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="pt-4 border-t border-neutral-100" data-testid="billing-section">
      <h3 className="font-heading font-bold mb-1 flex items-center gap-2">
        <Landmark className="w-4 h-4 text-neutral-400" /> Facturation
      </h3>
      <p className="text-sm text-neutral-500 mb-3">
        Coordonnées utilisées sur les devis et factures (figées dans chaque document émis), et génération automatique de la facture mensuelle.
      </p>
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nom / prénom du représentant</Label>
            <Input value={contact} onChange={(e) => setContact(e.target.value)} className="mt-1" data-testid="billing-contact" placeholder="Jean Dupont" />
          </div>
          <div>
            <Label className="text-xs">Email de facturation (utilisé pour l'envoi automatique mensuel)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="billing-email" placeholder="compta@client.com" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nom entreprise (fiche client)</Label>
            <Input value={client.name} disabled className="mt-1 bg-neutral-50 text-neutral-500" data-testid="billing-company" />
          </div>
          <div>
            <Label className="text-xs">SIRET (optionnel)</Label>
            <Input value={siret} onChange={(e) => setSiret(e.target.value)} className="mt-1" data-testid="billing-siret" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Adresse de facturation</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1" data-testid="billing-address" placeholder="12 rue… 75000 Paris" />
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 space-y-3" data-testid="auto-invoice-config">
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" checked={auto.enabled} onChange={(e) => setAuto((a) => ({ ...a, enabled: e.target.checked }))}
              className="w-4 h-4 accent-neutral-900" data-testid="auto-invoice-enabled" />
            <span className="font-medium">Facture automatique mensuelle</span>
          </label>
          {auto.enabled && (
            <>
              <p className="text-xs text-neutral-500">
                La facture est générée en brouillon le 25 de chaque mois (notification unique « Vos factures mensuelles sont prêtes pour validation »), puis envoyée automatiquement au client le {auto.day} une fois validée, avec les lignes détaillées ci-dessous.
                {items.length === 0 && monthlyTotal > 0 && (
                  <> À défaut de lignes, les éléments « Forfait mensuel » des données financières (<strong>{monthlyTotal} €</strong>) seront repris.</>
                )}
              </p>

              <div>
                <Label className="text-xs">Titre automatique de la facture (optionnel)</Label>
                <Input value={auto.title_template || ""} onChange={(e) => setAuto((a) => ({ ...a, title_template: e.target.value }))}
                  className="mt-1" placeholder="Community management - {{mois_en_cours}}" data-testid="auto-title-template" />
                <p className="text-[11px] text-neutral-400 mt-1">
                  Variables : {"{{mois_en_cours}}"} (Août), {"{{annee_en_cours}}"} (2026), {"{{mois_annee}}"} (Août 2026), {"{{nom_client}}"}.
                  Elles sont remplacées à la création de la facture, qui conserve ensuite son titre même si ce modèle change.
                </p>
              </div>

              <div className="space-y-2" data-testid="auto-invoice-items">
                <Label className="text-xs">Lignes de la facture mensuelle</Label>
                {items.map((it, i) => (
                  <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2 bg-white" data-testid={`auto-item-${i}`}>
                    <div className="flex gap-2">
                      <Input placeholder="Désignation (ex : Community Management)" value={it.prestation}
                        onChange={(e) => setItem(i, "prestation", e.target.value)} className="flex-1" data-testid={`auto-item-prestation-${i}`} />
                      {items.length > 1 && (
                        <button onClick={() => setItems((its) => its.filter((_, j) => j !== i))}
                          className="text-neutral-300 hover:text-red-500" data-testid={`auto-item-remove-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Textarea placeholder={"Description (optionnel) — paragraphes et retours à la ligne acceptés"} value={it.description || ""}
                      rows={2} className="text-sm"
                      onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`auto-item-description-${i}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Quantité</Label>
                        <Input type="number" min="0.5" step="0.5" value={it.qty}
                          onChange={(e) => setItem(i, "qty", e.target.value)} className="mt-1" data-testid={`auto-item-qty-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Prix unitaire HT (€)</Label>
                        <Input type="number" min="0" value={it.price_ht}
                          onChange={(e) => setItem(i, "price_ht", e.target.value)} className="mt-1" data-testid={`auto-item-price-${i}`} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setItems((its) => [...its, emptyItem()])}
                  className="gap-1.5" data-testid="auto-item-add">
                  <Plus className="w-4 h-4" /> Ajouter une ligne
                </Button>
                {itemsTotal > 0 && (
                  <p className="text-xs text-neutral-500" data-testid="auto-invoice-total">
                    Total mensuel : <strong>{eur(itemsTotal)} HT</strong>
                    {tva === "tva" ? ` — soit ${eur(itemsTotal * 1.2)} TTC` : " (TVA non applicable, art. 293 B du CGI)"}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Jour du mois (1-28)</Label>
                  <Input type="number" min="1" max="28" value={auto.day}
                    onChange={(e) => setAuto((a) => ({ ...a, day: parseInt(e.target.value) || 1 }))} className="mt-1" data-testid="auto-invoice-day" />
                </div>
                <div>
                  <Label className="text-xs">TVA</Label>
                  <select className={`${sel} mt-1`} value={tva} onChange={(e) => setTva(e.target.value)} data-testid="auto-invoice-tva">
                    <option value="non_assujetti">Non assujetti</option>
                    <option value="tva">TVA 20 %</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
                Rien ne part sans vous : la facture est générée en brouillon le 25 (notification), et n'est envoyée au client ({email || "email de facturation"}) le jour J que si vous l'avez validée entre-temps.
              </p>
              <Button variant="outline" size="sm" onClick={generateNow} disabled={genBusy}
                className="gap-1.5" data-testid="auto-invoice-generate-now">
                <FilePlus2 className="w-4 h-4" /> {genBusy ? "Génération…" : "Générer le brouillon de ce cycle maintenant"}
              </Button>
            </>
          )}
        </div>

        <Button onClick={save} disabled={saving} size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="billing-save">
          {saving ? "Enregistrement…" : "Enregistrer la facturation"}
        </Button>
      </div>
    </div>
  );
}
